import { useEffect, useMemo, useRef, useState } from "react"
import { useSnapshot } from "valtio"
import Api from "../state/Api"
import { AgmodalStore, configStore, loginStore } from "../state/CommonState"
import { taskStore } from "./task-store"
import Taskapi from "./tasks-api"

const POLL_MS = 10000

const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
const amt = (v) => (typeof v === 'number' ? v : typeof v?.amount === 'number' ? v.amount : null)
const round2 = (n) => Math.round(n * 100) / 100
const parseMoney = (s) => (s == null ? null : Number(String(s).replace(/[^0-9.]/g, '')) || null)
const parsePercent = (s) => (s == null ? null : Number(String(s).replace(/[^0-9.]/g, '')) / 100 || null)
// The classifier's warning objects vary in shape; show their own words
const flagText = (f) => (typeof f === 'string' ? f : f?.message || f?.reason || f?.note || f?.detail || JSON.stringify(f))

// Every code a line can be given: ranked results, then the description-only candidates of an HSN/description mismatch
function lineCandidates(line) {
    const c = line.classification || {}
    const alt = c.hsnMismatch?.results || c.hsnMismatch?.candidates || []
    return [...(c.results || []).map(r => ({ ...r, from: 'results' })), ...alt.map(r => ({ ...r, from: 'hsnMismatch' }))]
}

// Same calculation as agserver util/htsClassify.js computeTotals, for the live preview; the server's figure is the one saved
function computeTotals(rec, selections) {
    const byLine = new Map(selections.map(s => [s.lineNumber, s]))
    const lines = rec.lines || []
    const uncomputed = []
    const caveats = new Set()
    let customsDuty = 0, sectionSurcharges = 0, cargo = 0
    for (const line of lines) {
        if (line.skipReason) { uncomputed.push({ lineNumber: line.lineNumber, reason: line.skipReason }); continue }
        const sel = byLine.get(line.lineNumber)
        if (!sel?.candidate) {
            const reason = lineCandidates(line).length ? 'No HTS code selected' : line.classification?.message || 'The classifier returned no HTS code'
            uncomputed.push({ lineNumber: line.lineNumber, reason }); continue
        }
        if (!line.cargoValueUsd) { uncomputed.push({ lineNumber: line.lineNumber, reason: 'No line value in a known currency' }); continue }
        cargo += line.cargoValueUsd
        const lc = sel.candidate.landedCost
        if (!lc) { uncomputed.push({ lineNumber: line.lineNumber, reason: 'Priced when submitted' }); continue }
        const duty = lc.customsDuty?.computable === false ? null : amt(lc.customsDuty)
        const rate = sel.candidate.duty?.rateApplicable || lc.customsDuty?.rateApplicable || ''
        if (duty == null) uncomputed.push({ lineNumber: line.lineNumber, reason: `Customs duty ${rate} is charged per quantity, not on value`.replace('  ', ' ') })
        else customsDuty += duty
        sectionSurcharges += amt(lc.sectionSurcharges) || 0
        if (lc.sectionSurcharges?.complete === false) uncomputed.push({ lineNumber: line.lineNumber, reason: 'Section tariff amount incomplete' })
        ;(lc.sectionSurcharges?.caveats || []).forEach(c => caveats.add(c))
    }
    const entries = rec.entries || []
    let mpf = null, hmf = null
    if (entries.length === 1) {
        mpf = amt(entries[0].entrySummary?.mpf)
        hmf = amt(entries[0].entrySummary?.hmf)
    } else if (entries.length > 1) {
        hmf = entries.reduce((s, e) => s + (amt(e.entrySummary?.hmf) || 0), 0)
        const fee = entries.map(e => e.surcharges?.mpf).find(f => f?.rateDecimal || f?.rate)
        const rate = fee?.rateDecimal ?? parsePercent(fee?.rate)
        if (rate) {
            const value = entries.reduce((s, e) => s + (amt(e.entrySummary?.totalCargoValue) || 0), 0)
            mpf = Math.min(Math.max(value * rate, fee.minimumAmount ?? parseMoney(fee.minimum) ?? 0), fee.maximumAmount ?? parseMoney(fee.maximum) ?? Infinity)
        } else mpf = entries.reduce((s, e) => s + (amt(e.entrySummary?.mpf) || 0), 0)
    }
    const total = customsDuty + sectionSurcharges + (mpf || 0) + (hmf || 0)
    return {
        totalCargoValue: round2(cargo), customsDuty: round2(customsDuty), sectionSurcharges: round2(sectionSurcharges),
        mpf: mpf == null ? null : round2(mpf), hmf: hmf == null ? null : round2(hmf),
        totalDutiesAndFees: round2(total), totalLandedCost: round2(cargo + total),
        effectiveRatePercent: cargo ? round2((total / cargo) * 100) : null,
        complete: uncomputed.length === 0 && mpf != null, uncomputed, caveats: [...caveats],
    }
}

function Warnings({ line }) {
    const c = line.classification || {}
    const items = []
    if (c.discrepancyFlag) items.push(['Review the code', 'The description does not clearly match the coded article: ' + flagText(c.discrepancyFlag)])
    if (c.hsnMismatch) items.push(['HS code and description disagree', 'Candidates read from the description alone are listed separately below. ' + (c.hsnMismatch.retrievedBy === 'catalogue' ? 'They were chosen from the whole schedule, a weaker starting point.' : '')])
    if (c.scopeWarning) items.push(['Mixed shipment', flagText(c.scopeWarning) + ((c.scopeWarning.suggestions || []).length ? ' — codes for a separate line: ' + c.scopeWarning.suggestions.map(s => s.htsCode || s).join(', ') : '')])
    if (!(c.results || []).length && c.message) items.push(['No classification', c.message])
    if (!items.length) return <></>
    return (
        <div className="notification is-warning is-light" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.5rem' }}>
            {items.map(([title, text]) => <p key={title}><strong>{title}:</strong> {text}</p>)}
        </div>
    )
}

function CandidateRows({ line, candidates, picked, onPick, readOnly }) {
    return candidates.map(c => {
        const lc = c.landedCost
        const sections = (c.duty?.additionalTariffAdvisories || []).filter(a => a.status === 'active')
        const id = `hts-${line.lineNumber}-${c.from}-${c.htsCode}`
        return (
            <tr key={id} className={picked === c.htsCode ? 'is-selected' : ''} style={picked === c.htsCode ? { background: '#eef6ff', color: 'inherit' } : {}}>
                <td>
                    <input type="radio" id={id} name={`hts-line-${line.lineNumber}`} checked={picked === c.htsCode} disabled={readOnly}
                        onChange={() => onPick(line.lineNumber, c.htsCode)} />
                </td>
                <td>{c.from === 'results' ? c.rank : '—'}</td>
                <td><label htmlFor={id} style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{c.htsCode}</label></td>
                <td style={{ minWidth: '16rem' }}>
                    {c.articleDescription}
                    {c.matchReasoning && <p className="is-size-7 has-text-grey">{c.matchReasoning}</p>}
                </td>
                <td>{c.duty?.rateApplicable || '—'}</td>
                <td>
                    {sections.length ? sections.map(a => <div key={a.chapter99Code} className="is-size-7">{a.section} +{a.additionalRate}{a.verified === false ? ' (unverified)' : ''}</div>) : '—'}
                </td>
                <td className="has-text-right">
                    {lc ? <>{money(amt(lc.totalDutiesAndFees))}{lc.totalDutiesAndFees?.complete === false && <span className="tag is-warning is-light" style={{ marginLeft: 4 }}>incomplete</span>}</>
                        : line.cargoValueUsd ? <span className="is-size-7 has-text-grey">priced on submit</span> : '—'}
                </td>
                <td className="is-size-7">{c.matchConfidence != null ? `${Math.round(c.matchConfidence * 100)}% ${c.confidenceBasis || ''}` : ''}</td>
            </tr>
        )
    })
}

function Line({ line, picked, onPick, readOnly }) {
    const all = lineCandidates(line)
    const ranked = all.filter(c => c.from === 'results')
    const alt = all.filter(c => c.from === 'hsnMismatch')
    return (
        <div className="box" style={{ padding: '0.75rem', marginBottom: '0.75rem' }}>
            <p style={{ marginBottom: '0.5rem' }}>
                <strong>Line {line.lineNumber}</strong>
                {line.invoiceNumber && <> · Invoice {line.invoiceNumber}</>}
                {' · '}HS {line.hsCodeRaw || line.hsCode || '—'}
                {' · '}{line.description}
                {' · '}{line.value?.amount != null ? `${Number(line.value.amount).toLocaleString()} ${line.value.currency || ''}` : 'no value'}
                {line.cargoValueUsd && line.value?.currency !== 'USD' ? ` (≈ ${money(line.cargoValueUsd)})` : ''}
                {line.originCountry && <> · Origin {line.originCountry}</>}
            </p>
            {line.skipReason ? <p className="has-text-danger">Not classified: {line.skipReason}</p> : (
                <>
                    <Warnings line={line} />
                    {all.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table is-bordered is-narrow is-fullwidth">
                                <thead>
                                    <tr><th></th><th>Rank</th><th>HTS code</th><th>Description</th><th>Duty rate</th><th>Section tariffs</th><th className="has-text-right">Line duties &amp; fees</th><th>Match</th></tr>
                                </thead>
                                <tbody>
                                    <CandidateRows line={line} candidates={ranked} picked={picked} onPick={onPick} readOnly={readOnly} />
                                    {alt.length > 0 && <tr><td colSpan={8} className="has-text-grey is-size-7">From the description alone (HS code and description disagree)</td></tr>}
                                    <CandidateRows line={line} candidates={alt} picked={picked} onPick={onPick} readOnly={readOnly} />
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

function Totals({ totals, dataVintage, disclaimer }) {
    if (!totals) return <></>
    const row = (label, value, strong) => <tr><td>{strong ? <strong>{label}</strong> : label}</td><td className="has-text-right">{strong ? <strong>{money(value)}</strong> : money(value)}</td></tr>
    return (
        <div className="box" style={{ padding: '0.75rem' }}>
            <h6 className="title is-6" style={{ marginBottom: '0.5rem' }}>
                Customs Duties &amp; Taxes (USD){' '}
                {!totals.complete && <span className="tag is-warning is-light">incomplete</span>}
            </h6>
            <table className="table is-narrow is-fullwidth" style={{ maxWidth: '32rem' }}>
                <tbody>
                    {row('Cargo value', totals.totalCargoValue)}
                    {row('Customs duty', totals.customsDuty)}
                    {row('Section 232 / 301 / IEEPA tariffs', totals.sectionSurcharges)}
                    {row('Merchandise Processing Fee (MPF)', totals.mpf)}
                    {row('Harbor Maintenance Fee (HMF)', totals.hmf)}
                    {row('Total duties & taxes', totals.totalDutiesAndFees, true)}
                    {row('Landed cost', totals.totalLandedCost)}
                    {totals.effectiveRatePercent != null && <tr><td>Effective rate</td><td className="has-text-right">{totals.effectiveRatePercent}%</td></tr>}
                </tbody>
            </table>
            {totals.uncomputed?.length > 0 && <p className="is-size-7 has-text-warning-dark">Not included: {totals.uncomputed.map(u => `line ${u.lineNumber} (${u.reason})`).join('; ')}</p>}
            {totals.caveats?.length > 0 && <p className="is-size-7 has-text-grey">Section tariffs are maximum exposure and may not apply: {totals.caveats.join('; ')}</p>}
            <p className="is-size-7 has-text-grey">{dataVintage ? `US tariff schedule ${dataVintage}. ` : ''}{disclaimer || 'Advisory only; not customs or legal advice.'}</p>
        </div>
    )
}

// HTS Classification My Action: the customer (or CS on their behalf) picks the US HTS code of every CI product line;
// customs duties and taxes are calculated from the picks. Classification runs on the server in the background.
export default function HtsClassification() {
    useSnapshot(taskStore)
    const task = taskStore.current
    const [rec, setRec] = useState(null)
    const [job, setJob] = useState(null)
    const [picks, setPicks] = useState({})
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const pollTimer = useRef(null)
    const session = useRef(0)

    function load() {
        const s = session.current
        Api.getHtsClassification({ bookingNum: task.bookingNum, taskNum: task._id?.num }, (res) => {
            if (s !== session.current) return
            if (!res.success) return setError(res.message || 'Could not load the HTS classification')
            setRec(res.data)
            setJob(res.job)
            if (res.data?.status === 'pending' || ['pending', 'running'].includes(res.job?.status)) {
                clearTimeout(pollTimer.current)
                pollTimer.current = setTimeout(() => s === session.current && load(), POLL_MS)
            }
        })
    }

    useEffect(() => {
        if (!task?.bookingNum) return
        session.current++
        load()
        return () => {
            session.current++
            clearTimeout(pollTimer.current)
        }
    }, [task?.bookingNum, task?._id?.num])

    // Best match preselected once the lines are classified; the user reviews and changes it before submitting
    useEffect(() => {
        if (rec?.status !== 'classified') return
        setPicks(prev => {
            const next = { ...prev }
            for (const l of rec.lines || []) {
                if (!l.skipReason && !next[l.lineNumber] && l.classification?.results?.[0]) next[l.lineNumber] = l.classification.results[0].htsCode
            }
            return next
        })
    }, [rec?._id])

    const submitted = rec?.status === 'submitted' || task.status === 'COMPLETED'
    const selections = useMemo(() => {
        if (!rec) return []
        if (rec.status === 'submitted') return rec.selections || []
        return (rec.lines || []).filter(l => picks[l.lineNumber]).map(l => ({
            lineNumber: l.lineNumber, htsCode: picks[l.lineNumber],
            candidate: lineCandidates(l).find(c => c.htsCode === picks[l.lineNumber]),
        }))
    }, [rec, picks])
    const totals = rec?.status === 'submitted' ? rec.totals : rec?.status === 'classified' ? computeTotals(rec, selections) : null
    const pickedOf = (lineNumber) => (rec?.status === 'submitted' ? rec.selections?.find(s => s.lineNumber === lineNumber)?.htsCode : picks[lineNumber])
    const missing = (rec?.lines || []).filter(l => !l.skipReason && lineCandidates(l).length > 0 && !picks[l.lineNumber])

    async function submit() {
        setSaving(true)
        setError('')
        Api.submitHtsClassification({
            bookingNum: task.bookingNum, taskNum: task._id?.num,
            selections: Object.entries(picks).map(([lineNumber, htsCode]) => ({ lineNumber: Number(lineNumber), htsCode })),
        }, async (res) => {
            if (!res.success) {
                setSaving(false)
                return setError(res.message || 'Could not save the selection')
            }
            setRec(res.data)
            taskStore.current.stage = 2
            taskStore.setCustomerComplete()
            taskStore.setComplete()
            await Taskapi.updateTask(taskStore.current)
            setSaving(false)
            taskStore.triggerActionVisible = false
            taskStore.myActionVisible = false
            if (AgmodalStore.modalPage === "TaskContainer" || loginStore.isClient()) {
                AgmodalStore.pageVisible = false
                AgmodalStore.apiCall = true
            }
        }, () => setSaving(false))
    }

    function reclassify() {
        Api.reclassifyHts({ bookingNum: task.bookingNum, taskNum: task._id?.num }, (res) => {
            if (!res.success) return configStore.setModalMessage(res.message || 'Could not classify again')
            load()
        })
    }

    if (!rec) return <div className="box">{error ? <p className="has-text-danger">{error}</p> : <p>Loading HTS classification...</p>}</div>

    const classifiable = (rec.lines || []).filter(l => !l.skipReason).length
    return (
        <div>
            <div className="box" style={{ padding: '0.75rem' }}>
                <strong>HTS Classification (US)</strong>
                <p className="is-size-7">
                    {submitted
                        ? `Submitted${rec.submittedBy?.email ? ' by ' + rec.submittedBy.email : ''}${rec.submittedOn ? ' on ' + new Date(rec.submittedOn).toLocaleString() : ''}.`
                        : `Select the US HTS code for each product line of the Commercial Invoice. The best match is preselected; change it where needed. Duties and taxes follow from your selection.`}
                    {!submitted && !loginStore.isClient() && ' You are selecting on the customer\'s behalf.'}
                </p>
            </div>
            {rec.status === 'pending' && (
                <div className="notification is-info is-light">
                    Classifying {classifiable} product line{classifiable === 1 ? '' : 's'} against the US Harmonized Tariff Schedule
                    {job?.status === 'running' && job.startedOn ? ` - running for ${Math.max(1, Math.round((Date.now() - new Date(job.startedOn)) / 60000))} min` : ''}.
                    This takes a few seconds per line; the result shows here by itself.
                </div>
            )}
            {rec.status === 'failed' && (
                <div className="notification is-danger is-light">
                    <p>Classification failed: {rec.error}</p>
                    {job?.status === 'pending' && job.notBefore
                        ? <p>It will be tried again automatically at {new Date(job.notBefore).toLocaleTimeString()}.</p>
                        : <button className="button is-small islink" style={{ marginTop: '0.5rem' }} onClick={reclassify}>Classify again</button>}
                </div>
            )}
            {['classified', 'submitted'].includes(rec.status) && (
                <>
                    {(rec.lines || []).map(l => (
                        <Line key={l.lineNumber} line={l} picked={pickedOf(l.lineNumber)} readOnly={submitted}
                            onPick={(lineNumber, htsCode) => setPicks(p => ({ ...p, [lineNumber]: htsCode }))} />
                    ))}
                    <Totals totals={totals} dataVintage={rec.dataVintage} disclaimer={rec.disclaimer} />
                    {!submitted && (
                        <p>
                            {error && <span className="has-text-danger" style={{ display: 'block', marginBottom: '0.5rem' }}>{error}</span>}
                            <button className={'button islink' + (saving ? ' is-loading' : '')} disabled={saving || missing.length > 0} onClick={submit}>
                                Submit
                            </button>
                            {missing.length > 0 && <span className="is-size-7 has-text-grey" style={{ marginLeft: '0.75rem' }}>Select a code for line {missing.map(l => l.lineNumber).join(', ')}</span>}
                        </p>
                    )}
                </>
            )}
        </div>
    )
}
